import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import { format } from 'date-fns';
import { Button, Chip, Tooltip, Typography } from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarPlus, faEdit, faTrashAlt, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import { Alerty } from '../../common/Alerty';

const CarInsuranceExpiry = () => {
  const { token, user } = useAuth();
  const [insurances, setInsurances] = useState([]);
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);
  const [insuranceTypes, setInsuranceTypes] = useState([]);
  const [carPlate, setCarPlate] = useState('');
  const [editingId, setEditingId] = useState(null);
  
  // Form fields
  const [insuranceCompany, setInsuranceCompany] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [insuranceType, setInsuranceType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [premium, setPremium] = useState('');
  const [valuation, setValuation] = useState('');
  const [notes, setNotes] = useState('');
  const [valuationReportLink, setValuationReportLink] = useState('');
  const [insuranceStickerLink, setInsuranceStickerLink] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [contactPhoneNumber, setContactPhoneNumber] = useState('');
  
  const [alert, setAlert] = useState({ color: '', message: '' });
  const [loading, setLoading] = useState(false);

  const plateFromUrl = window.location.pathname.split('/').pop();

  const fetchInsuranceData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${rootUrl}/cars/insurance/plate/${plateFromUrl}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInsurances(response.data || []);
    } catch (error) {
      console.error('Error fetching insurance info:', error);
      setAlert({ color: 'red', message: 'Failed to fetch insurance data.' });
    } finally {
      setLoading(false);
    }
  }, [plateFromUrl, token]);

  const fetchInsuranceCompanies = useCallback(async () => {
    try {
      const response = await axios.get(`${rootUrl}/insurance/companies`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInsuranceCompanies(response.data || []);
    } catch (error) {
      console.error('Error fetching insurance companies:', error);
    }
  }, [token]);

  const fetchInsuranceTypes = useCallback(async () => {
    try {
      const response = await axios.get(`${rootUrl}/insurance/types`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInsuranceTypes(response.data || []);
    } catch (error) {
      console.error('Error fetching insurance types:', error);
    }
  }, [token]);

  const resetForm = () => {
    setInsuranceCompany('');
    setPolicyNumber('');
    setInsuranceType('');
    setStartDate('');
    setEndDate('');
    setPremium('');
    setValuation('');
    setNotes('');
    setValuationReportLink('');
    setInsuranceStickerLink('');
    setContactPerson('');
    setContactPhoneNumber('');
    setEditingId(null);
  };

  const handleSaveInsurance = async (e) => {
    e.preventDefault();

    // Validate required fields
    const requiredFields = {
      'Insurance Company': insuranceCompany,
      'Policy Number': policyNumber,
      'Insurance Type': insuranceType,
      'Start Date': startDate,
      'End Date': endDate
    };

    const missingFields = Object.entries(requiredFields)
      .filter(([key, value]) => !value)
      .map(([key]) => key);

    if (missingFields.length > 0) {
      setAlert({
        color: 'amber',
        message: `Please fill in all required fields: ${missingFields.join(', ')}`,
      });
      return;
    }

    // Validate date logic
    if (new Date(endDate) <= new Date(startDate)) {
      setAlert({
        color: 'amber',
        message: 'End date must be after start date.',
      });
      return;
    }

    try {
      const payload = {
          plate_no: plateFromUrl,
        insurance_company: insuranceCompany,
        policy_number: policyNumber,
        insurance_type: insuranceType,
        start_date: startDate,
        end_date: endDate,
        premium: premium ? parseFloat(premium) : null,
        valuation: valuation ? parseFloat(valuation) : null,
        notes: notes || null,
        valuation_report_link: valuationReportLink || null,
        insurance_sticker_link: insuranceStickerLink || null,
        contact_person: contactPerson || null,
        contact_phone_number: contactPhoneNumber || null,
          input_by: user?.username,
      };

      if (editingId) {
        // Update existing record
        payload.edit_by = user?.username;
        await axios.put(`${rootUrl}/cars/insurance/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAlert({ color: 'green', message: 'Insurance record updated successfully!' });
      } else {
        // Create new record
        await axios.post(`${rootUrl}/cars/insurance`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      setAlert({ color: 'green', message: 'Insurance record saved successfully!' });
      }

      resetForm();
      fetchInsuranceData();
    } catch (error) {
      console.error('Error saving insurance:', error);
      setAlert({
        color: 'red',
        message: error.response?.data?.error || 'Failed to save insurance record.',
      });
    }
  };

  const handleEditInsurance = (insurance) => {
    setEditingId(insurance.id);
    setInsuranceCompany(insurance.insurance_company || '');
    setPolicyNumber(insurance.policy_number || '');
    setInsuranceType(insurance.insurance_type || '');
    setStartDate(insurance.start_date ? insurance.start_date.split('T')[0] : '');
    setEndDate(insurance.end_date ? insurance.end_date.split('T')[0] : '');
    setPremium(insurance.premium || '');
    setValuation(insurance.valuation || '');
    setNotes(insurance.notes || '');
    setValuationReportLink(insurance.valuation_report_link || '');
    setInsuranceStickerLink(insurance.insurance_sticker_link || '');
    setContactPerson(insurance.contact_person || '');
    setContactPhoneNumber(insurance.contact_phone_number || '');
  };

  const handleDeleteInsurance = async (id) => {
    if (!window.confirm('Are you sure you want to delete this insurance record?')) return;

    try {
      await axios.delete(`${rootUrl}/cars/insurance/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlert({ color: 'green', message: 'Insurance record deleted successfully!' });
      fetchInsuranceData();
    } catch (error) {
      console.error('Error deleting insurance record:', error);
      setAlert({ color: 'red', message: 'Failed to delete insurance record.' });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setCarPlate(plateFromUrl);
    fetchInsuranceData();
    fetchInsuranceCompanies();
    fetchInsuranceTypes();
    setStartDate(new Date().toISOString().split('T')[0]);
  }, [fetchInsuranceData, fetchInsuranceCompanies, fetchInsuranceTypes]);

  return (
    <div className="min-h-screen p-3 sm:p-6">
      <div className="mb-4">
        <a href="/cars" className="text-orange-500 hover:text-orange-700 text-sm flex items-center">
          <FontAwesomeIcon icon={faShieldAlt} className="mr-1" />
          Back to Cars
        </a>
      </div>

      <h1 className="text-lg sm:text-2xl font-bold text-[#2a2e30] mb-4 flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="flex items-center">
          <FontAwesomeIcon icon={faShieldAlt} className="text-[#FF5733] mr-2" />
          Insurance Details
        </div>
        <a href={`/cars/${carPlate}`} className="text-orange-500 hover:text-orange-700 text-sm sm:text-base">
          Plate: {carPlate}
        </a>
      </h1>

      {alert && alert.message && (
        <Alerty open={alert.message} setOpen={setAlert} color={alert.color} message={alert.message} />
      )}

      {/* FORM */}
      <form onSubmit={handleSaveInsurance} className="bg-white shadow-md rounded-lg p-4 sm:p-6 mb-6">
        <h3 className="text-base sm:text-lg font-semibold text-[#2a2e30] mb-4">
          {editingId ? 'Edit Insurance Record' : 'Add New Insurance Record'}
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4">
          <div>
            <label htmlFor="insuranceCompany" className="block text-sm font-medium text-[#2a2e30] mb-1">
              Insurance Company *
            </label>
            <select
              id="insuranceCompany"
              value={insuranceCompany}
              onChange={(e) => setInsuranceCompany(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm 
              focus:outline-none focus:ring-2 focus:ring-[#FF5733] focus:border-[#FF5733]
              min-h-[44px] touch-manipulation"
              required
            >
              <option value="">-- Select Company --</option>
              {insuranceCompanies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="policyNumber" className="block text-sm font-medium text-[#2a2e30] mb-1">
              Policy Number *
            </label>
            <input
              id="policyNumber"
              type="text"
              value={policyNumber}
              onChange={(e) => setPolicyNumber(e.target.value)}
              placeholder="Enter policy number"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm 
              focus:outline-none focus:ring-2 focus:ring-[#FF5733] focus:border-[#FF5733]
              min-h-[44px] touch-manipulation"
              required
            />
          </div>

          <div>
            <label htmlFor="insuranceType" className="block text-sm font-medium text-[#2a2e30] mb-1">
              Insurance Type *
            </label>
            <select
              id="insuranceType"
              value={insuranceType}
              onChange={(e) => setInsuranceType(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm 
              focus:outline-none focus:ring-2 focus:ring-[#FF5733] focus:border-[#FF5733]
              min-h-[44px] touch-manipulation"
              required
            >
              <option value="">-- Select Type --</option>
              {insuranceTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-[#2a2e30] mb-1">
              Start Date *
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm 
              focus:outline-none focus:ring-2 focus:ring-[#FF5733] focus:border-[#FF5733]
              min-h-[44px] touch-manipulation"
              required
            />
          </div>

          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-[#2a2e30] mb-1">
              End Date *
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm 
              focus:outline-none focus:ring-2 focus:ring-[#FF5733] focus:border-[#FF5733]
              min-h-[44px] touch-manipulation"
              required
            />
          </div>

          <div>
            <label htmlFor="premium" className="block text-sm font-medium text-[#2a2e30] mb-1">
              Premium Amount
            </label>
            <input
              id="premium"
              type="number"
              step="0.01"
              value={premium}
              onChange={(e) => setPremium(e.target.value)}
              placeholder="0.00"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm 
              focus:outline-none focus:ring-2 focus:ring-[#FF5733] focus:border-[#FF5733]
              min-h-[44px] touch-manipulation"
            />
          </div>

          <div>
            <label htmlFor="valuation" className="block text-sm font-medium text-[#2a2e30] mb-1">
              Vehicle Valuation
            </label>
            <input
              id="valuation"
              type="number"
              step="0.01"
              value={valuation}
              onChange={(e) => setValuation(e.target.value)}
              placeholder="0.00"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm 
              focus:outline-none focus:ring-2 focus:ring-[#FF5733] focus:border-[#FF5733]
              min-h-[44px] touch-manipulation"
            />
          </div>

          <div>
            <label htmlFor="contactPerson" className="block text-sm font-medium text-[#2a2e30] mb-1">
              Contact Person
            </label>
            <input
              id="contactPerson"
              type="text"
              value={contactPerson}
              onChange={(e) => setContactPerson(e.target.value)}
              placeholder="Contact person name"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm 
              focus:outline-none focus:ring-2 focus:ring-[#FF5733] focus:border-[#FF5733]
              min-h-[44px] touch-manipulation"
            />
          </div>

          <div>
            <label htmlFor="contactPhoneNumber" className="block text-sm font-medium text-[#2a2e30] mb-1">
              Contact Phone
            </label>
            <input
              id="contactPhoneNumber"
              type="tel"
              value={contactPhoneNumber}
              onChange={(e) => setContactPhoneNumber(e.target.value)}
              placeholder="Phone number"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm 
              focus:outline-none focus:ring-2 focus:ring-[#FF5733] focus:border-[#FF5733]
              min-h-[44px] touch-manipulation"
            />
          </div>

          <div>
            <label htmlFor="valuationReportLink" className="block text-sm font-medium text-[#2a2e30] mb-1">
              Valuation Report Link
            </label>
            <input
              id="valuationReportLink"
              type="url"
              value={valuationReportLink}
              onChange={(e) => setValuationReportLink(e.target.value)}
              placeholder="https://example.com/valuation-report"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm 
              focus:outline-none focus:ring-2 focus:ring-[#FF5733] focus:border-[#FF5733]
              min-h-[44px] touch-manipulation"
            />
          </div>

          <div>
            <label htmlFor="insuranceStickerLink" className="block text-sm font-medium text-[#2a2e30] mb-1">
              Insurance Sticker Link
            </label>
            <input
              id="insuranceStickerLink"
              type="url"
              value={insuranceStickerLink}
              onChange={(e) => setInsuranceStickerLink(e.target.value)}
              placeholder="https://example.com/insurance-sticker"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm 
              focus:outline-none focus:ring-2 focus:ring-[#FF5733] focus:border-[#FF5733]
              min-h-[44px] touch-manipulation"
            />
          </div>

          <div className="sm:col-span-2 lg:col-span-3">
            <label htmlFor="notes" className="block text-sm font-medium text-[#2a2e30] mb-1">
              Notes
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes..."
              rows={3}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm 
              focus:outline-none focus:ring-2 focus:ring-[#FF5733] focus:border-[#FF5733]
              min-h-[44px] touch-manipulation resize-vertical"
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-2">
          {editingId && (
            <Button
              type="button"
              variant="outlined"
              color="gray"
              onClick={resetForm}
              className="flex items-center justify-center gap-2 min-h-[44px] touch-manipulation w-full sm:w-auto"
            >
              Cancel
            </Button>
          )}
          <Button 
            type="submit" 
            variant="outlined" 
            color="orange" 
            className="flex items-center justify-center gap-2 min-h-[44px] touch-manipulation w-full sm:w-auto"
          >
            <FontAwesomeIcon icon={editingId ? faEdit : faCalendarPlus} />
            {editingId ? 'Update' : 'Save'}
          </Button>
        </div>
      </form>

      {/* TABLE */}
      <h2 className="text-lg sm:text-xl font-bold text-[#2a2e30] mb-4">Insurance Records</h2>
      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <table className="w-full table-auto text-left min-w-[800px]">
          <thead>
            <tr>
              <th className="border-b bg-blue-gray-50 p-2 sm:p-4">
                <Typography variant="small" color="blue-gray" className="font-normal leading-none opacity-70 text-xs sm:text-sm">
                  Company
                </Typography>
              </th>
              <th className="border-b bg-blue-gray-50 p-2 sm:p-4">
                <Typography variant="small" color="blue-gray" className="font-normal leading-none opacity-70 text-xs sm:text-sm">
                  Policy
                </Typography>
              </th>
              <th className="border-b bg-blue-gray-50 p-2 sm:p-4">
                <Typography variant="small" color="blue-gray" className="font-normal leading-none opacity-70 text-xs sm:text-sm">
                  Type
                </Typography>
              </th>
              <th className="border-b bg-blue-gray-50 p-2 sm:p-4">
                <Typography variant="small" color="blue-gray" className="font-normal leading-none opacity-70 text-xs sm:text-sm">
                  Period
                </Typography>
              </th>
              <th className="border-b bg-blue-gray-50 p-2 sm:p-4">
                <Typography variant="small" color="blue-gray" className="font-normal leading-none opacity-70 text-xs sm:text-sm">
                  Premium
                </Typography>
              </th>
              <th className="border-b bg-blue-gray-50 p-2 sm:p-4">
                <Typography variant="small" color="blue-gray" className="font-normal leading-none opacity-70 text-xs sm:text-sm">
                  Contact
                </Typography>
              </th>
              <th className="border-b bg-blue-gray-50 p-2 sm:p-4">
                <Typography variant="small" color="blue-gray" className="font-normal leading-none opacity-70 text-xs sm:text-sm">
                  Links
                </Typography>
              </th>
              <th className="border-b bg-blue-gray-50 p-2 sm:p-4">
                <Typography variant="small" color="blue-gray" className="font-normal leading-none opacity-70 text-xs sm:text-sm">
                  Status
                </Typography>
              </th>
              <th className="border-b bg-blue-gray-50 p-2 sm:p-4">
                <Typography variant="small" color="blue-gray" className="font-normal leading-none opacity-70 text-xs sm:text-sm">
                  Actions
                </Typography>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="p-4 text-center text-gray-500">
                  Loading insurance data...
                </td>
              </tr>
            ) : insurances.length > 0 ? (
              insurances.map((ins) => {
                const isExpired = new Date(ins.end_date) < new Date();
                const isActive = new Date(ins.start_date) <= new Date() && new Date(ins.end_date) >= new Date();
                return (
                  <tr key={ins.id} className="even:bg-blue-gray-50/50">
                    <td className="p-2 sm:p-4">
                      <Typography variant="small" color="blue-gray" className="font-normal text-xs sm:text-sm">
                        {ins.insurance_company_name || 'N/A'}
                      </Typography>
                    </td>
                    <td className="p-2 sm:p-4">
                      <Typography variant="small" color="blue-gray" className="font-normal text-xs sm:text-sm">
                        {ins.policy_number}
                      </Typography>
                    </td>
                    <td className="p-2 sm:p-4">
                      <Typography variant="small" color="blue-gray" className="font-normal text-xs sm:text-sm">
                        {ins.insurance_type_name || 'N/A'}
                      </Typography>
                    </td>
                    <td className="p-2 sm:p-4">
                      <Typography variant="small" color="blue-gray" className="font-normal text-xs sm:text-sm">
                        <div className="hidden sm:block">
                          {format(new Date(ins.start_date), 'MMM dd, yyyy')} -{' '}
                          {format(new Date(ins.end_date), 'MMM dd, yyyy')}
                        </div>
                        <div className="block sm:hidden">
                          <div>{format(new Date(ins.start_date), 'MMM dd')}</div>
                          <div className="text-xs text-gray-500">to {format(new Date(ins.end_date), 'MMM dd')}</div>
                        </div>
                      </Typography>
                    </td>
                    <td className="p-2 sm:p-4">
                      <Typography variant="small" color="blue-gray" className="font-normal text-xs sm:text-sm">
                        {ins.premium ? new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'KES',
                          minimumFractionDigits: 2,
                        }).format(Number(ins.premium)) : 'N/A'}
                      </Typography>
                    </td>
                    <td className="p-2 sm:p-4">
                      <Typography variant="small" color="blue-gray" className="font-normal text-xs sm:text-sm">
                        {ins.contact_person ? (
                          <div>
                            <div className="font-medium">{ins.contact_person}</div>
                            {ins.contact_phone_number && (
                              <div className="text-xs text-gray-500">{ins.contact_phone_number}</div>
                            )}
                          </div>
                        ) : 'N/A'}
                      </Typography>
                    </td>
                    <td className="p-2 sm:p-4">
                      <div className="flex flex-col space-y-1">
                        {ins.valuation_report_link && (
                          <a
                            href={ins.valuation_report_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-xs underline touch-manipulation"
                          >
                            <span className="hidden sm:inline">Valuation Report</span>
                            <span className="sm:hidden">Report</span>
                          </a>
                        )}
                        {ins.insurance_sticker_link && (
                          <a
                            href={ins.insurance_sticker_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-xs underline touch-manipulation"
                          >
                            <span className="hidden sm:inline">Insurance Sticker</span>
                            <span className="sm:hidden">Sticker</span>
                          </a>
                        )}
                        {!ins.valuation_report_link && !ins.insurance_sticker_link && (
                          <span className="text-gray-500 text-xs">N/A</span>
                        )}
                      </div>
                    </td>
                    <td className="p-2 sm:p-4">
                      <div className="flex justify-center">
                        <Chip
                          size="sm"
                          color={isExpired ? 'red' : isActive ? 'green' : 'amber'}
                          value={isExpired ? 'EXPIRED' : isActive ? 'ACTIVE' : 'FUTURE'}
                          className="text-xs"
                        />
                      </div>
                    </td>
                    <td className="p-2 sm:p-4">
                      <div className="flex justify-center space-x-1 sm:space-x-2">
                        <Tooltip content="Edit" placement="top">
                          <button
                            className="text-orange-500 p-2 sm:p-1 hover:bg-orange-50 rounded touch-manipulation min-h-[44px] min-w-[44px] sm:min-h-auto sm:min-w-auto flex items-center justify-center"
                            onClick={() => handleEditInsurance(ins)}
                          >
                            <FontAwesomeIcon icon={faEdit} className="text-sm" />
                          </button>
                        </Tooltip>
                        <Tooltip content="Delete" placement="top">
                          <button
                            className="text-red-500 p-2 sm:p-1 hover:bg-red-50 rounded touch-manipulation min-h-[44px] min-w-[44px] sm:min-h-auto sm:min-w-auto flex items-center justify-center"
                            onClick={() => handleDeleteInsurance(ins.id)}
                          >
                            <FontAwesomeIcon icon={faTrashAlt} className="text-sm" />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="9" className="p-4 text-center text-gray-500">
                  No insurance records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CarInsuranceExpiry;
